/**
 * PDP Media Gallery — Photo / Video Tab Switcher
 *
 * Features:
 * - Tabs are shown only when the product has BOTH image and video media.
 * - Thumbnail strip is filtered by the active tab.
 * - Arrow (prev / next) clicks are intercepted so they navigate ONLY within the
 *   current tab's items, without touching media-gallery.js internals.
 * - Arrow disabled states are kept in sync with the current tab's position.
 * - Does NOT modify media-gallery.js; navigation is triggered by clicking
 *   the existing thumbnail buttons (gallery.js responds to those normally).
 */
(function () {
  'use strict';

  function initPdpMediaTabs(gallery) {
    if (gallery._pdpTabsInit) return;
    gallery._pdpTabsInit = true;

    var tabsBar = gallery.querySelector('[data-pdp-tabs]');
    var allThumbItems = Array.prototype.slice.call(
      gallery.querySelectorAll('.media-thumbs__item[data-pdp-tab]')
    );
    if (!allThumbItems.length) return;

    var tabBtns = tabsBar
      ? Array.prototype.slice.call(tabsBar.querySelectorAll('[data-pdp-tab-btn]'))
      : [];
    var dotsWrap = gallery.querySelector('[data-pdp-dots]');
    var allDots = Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-dot]'));
    var allViewerItems = Array.prototype.slice.call(
      gallery.querySelectorAll('.media-viewer__item[data-pdp-tab]')
    );
    var viewer = gallery.querySelector('.media-viewer');
    var thumbsStrip = gallery.querySelector('.media-thumbs');
    var prevArrow = gallery.querySelector('.media-ctrl__btn[name="prev"]');
    var nextArrow = gallery.querySelector('.media-ctrl__btn[name="next"]');
    var currentTab = 'photo';

    /* ── Helpers ───────────────────────────────────────────────── */

    function getVisibleItems() {
      return allThumbItems.filter(function (item) {
        return item.dataset.pdpTab === currentTab;
      });
    }

    /** Returns the index of the currently active thumb within visibleItems. */
    function getActiveIndex(visibleItems) {
      for (var i = 0; i < visibleItems.length; i++) {
        var btn = visibleItems[i].querySelector('.media-thumbs__btn');
        if (btn && btn.classList.contains('is-active')) return i;
      }
      return 0;
    }

    function getActiveMediaId() {
      var activeThumbBtn = gallery.querySelector('.media-thumbs__btn.is-active');
      var activeThumbItem = activeThumbBtn && activeThumbBtn.closest('.media-thumbs__item');
      return activeThumbItem ? activeThumbItem.dataset.mediaId : null;
    }

    function updateDotsVisibility() {
      if (!allDots.length) return;
      var visibleDotCount = 0;
      allDots.forEach(function (dot) {
        var isHidden = dot.dataset.pdpTab !== currentTab;
        dot.classList.toggle('pdp-tab-hidden', isHidden);
        if (!isHidden) visibleDotCount += 1;
      });
      if (dotsWrap) dotsWrap.hidden = visibleDotCount <= 1;
    }

    function refreshGalleryVisibleItems() {
      if (typeof gallery.setVisibleItems === 'function') {
        gallery.setVisibleItems();
      }
    }

    function applyTabVisibility(tabName) {
      allThumbItems.forEach(function (item) {
        item.classList.toggle('pdp-tab-hidden', item.dataset.pdpTab !== tabName);
      });
      allViewerItems.forEach(function (item) {
        item.classList.toggle('pdp-tab-hidden', item.dataset.pdpTab !== tabName);
      });
      updateDotsVisibility();
      refreshGalleryVisibleItems();
    }

    function updateDotsActive() {
      if (!allDots.length) return;
      var activeMediaId = getActiveMediaId();
      allDots.forEach(function (dot) {
        var isActive = dot.dataset.mediaId === activeMediaId;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function pauseAllVideosInGallery(hardStop) {
      if (typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia(gallery);
      }

      gallery.querySelectorAll('video').forEach(function (videoEl) {
        if (!videoEl.paused) {
          videoEl.pause();
        }
      });

      gallery.querySelectorAll('iframe').forEach(function (iframeEl) {
        var src = iframeEl.getAttribute('src') || '';
        if (!src) return;

        try {
          if (src.indexOf('youtube.com') !== -1 || src.indexOf('youtu.be') !== -1) {
            iframeEl.contentWindow.postMessage(
              JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
              '*'
            );
            return;
          }

          if (src.indexOf('vimeo.com') !== -1) {
            iframeEl.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
          }
        } catch (e) {
          // Ignore cross-origin or unavailable player messaging errors.
        }
      });

      if (!hardStop) return;

      // Hard-stop fallback for embedded players that ignore pause postMessage.
      gallery.querySelectorAll('.media-viewer__item[data-pdp-tab="video"] iframe').forEach(function (iframeEl) {
        var src = iframeEl.getAttribute('src');
        if (!src) return;
        iframeEl.setAttribute('src', src);
      });
    }

    function syncTabWithActiveMedia() {
      if (!tabsBar || tabsBar.hidden) return;
      var activeMediaId = getActiveMediaId();
      if (!activeMediaId) return;
      var activeThumbItem = gallery.querySelector('.media-thumbs__item[data-media-id="' + activeMediaId + '"]');
      if (!activeThumbItem) return;
      var activeTab = activeThumbItem.dataset.pdpTab;
      if (!activeTab || activeTab === currentTab) return;

      currentTab = activeTab;
      tabBtns.forEach(function (btn) {
        var isActive = btn.dataset.pdpTabBtn === activeTab;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      applyTabVisibility(activeTab);
    }

    var syncFromSwipeTimer = null;
    function scheduleSyncFromSwipe(delay) {
      if (syncFromSwipeTimer) clearTimeout(syncFromSwipeTimer);
      syncFromSwipeTimer = setTimeout(function () {
        syncTabWithActiveMedia();
        updateArrowStates();
        updateDotsActive();
      }, delay || 80);
    }

    /** Sync arrow disabled states to the current tab's position. */
    function updateArrowStates() {
      var visible = getVisibleItems();
      var idx = getActiveIndex(visible);
      if (prevArrow) {
        if (idx <= 0) prevArrow.setAttribute('disabled', '');
        else prevArrow.removeAttribute('disabled');
      }
      if (nextArrow) {
        if (idx >= visible.length - 1) nextArrow.setAttribute('disabled', '');
        else nextArrow.removeAttribute('disabled');
      }
    }

    /** Click a thumb item's button to navigate the main viewer. */
    function goToThumb(item) {
      var btn = item && item.querySelector('.media-thumbs__btn');
      if (!btn) return;
      btn.click();
      // Re-sync arrow states after gallery.js has had a chance to update
      setTimeout(function () {
        updateArrowStates();
        updateDotsActive();
      }, 60);
    }

    function initThumbDragScroll() {
      if (!thumbsStrip || thumbsStrip._pdpDragInit) return;
      thumbsStrip._pdpDragInit = true;

      var isPointerDown = false;
      var dragged = false;
      var startX = 0;
      var startScrollLeft = 0;
      var dragThreshold = 5;
      var lastClientX = 0;
      var framePending = false;
      var activePointerId = null;

      thumbsStrip.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });

      function flushScroll() {
        framePending = false;
        if (!isPointerDown) return;
        var deltaX = lastClientX - startX;
        thumbsStrip.scrollLeft = startScrollLeft - deltaX;
      }

      function onPointerMove(e) {
        if (!isPointerDown || e.pointerId !== activePointerId) return;
        lastClientX = e.clientX;
        var deltaX = lastClientX - startX;
        if (!dragged && Math.abs(deltaX) > dragThreshold) {
          dragged = true;
          thumbsStrip.classList.add('is-dragging');
        }
        if (!framePending) {
          framePending = true;
          window.requestAnimationFrame(flushScroll);
        }
        if (dragged) e.preventDefault();
      }

      function detachWindowListeners() {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerEnd);
        window.removeEventListener('pointercancel', onPointerEnd);
      }

      function onPointerEnd(e) {
        if (!isPointerDown || (e && e.pointerId !== activePointerId)) return;
        var wasDrag = dragged;
        isPointerDown = false;
        detachWindowListeners();
        activePointerId = null;
        thumbsStrip.classList.remove('is-dragging');
        if (wasDrag) {
          thumbsStrip.addEventListener(
            'click',
            function suppressThumbClickAfterDrag(ev) {
              ev.preventDefault();
              ev.stopPropagation();
            },
            { capture: true, once: true }
          );
        }
        dragged = false;
      }

      thumbsStrip.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        isPointerDown = true;
        dragged = false;
        activePointerId = e.pointerId;
        startX = e.clientX;
        lastClientX = e.clientX;
        startScrollLeft = thumbsStrip.scrollLeft;
        window.addEventListener('pointermove', onPointerMove, { passive: false });
        window.addEventListener('pointerup', onPointerEnd);
        window.addEventListener('pointercancel', onPointerEnd);
      });
    }

    /* ── Arrow interception (capture phase = before gallery.js) ─ */

    if (prevArrow) {
      prevArrow.addEventListener('click', function (e) {
        e.stopImmediatePropagation(); // prevent gallery.js from also handling
        var visible = getVisibleItems();
        var idx = getActiveIndex(visible);
        if (idx > 0) goToThumb(visible[idx - 1]);
      }, true /* capture */);
    }

    if (nextArrow) {
      nextArrow.addEventListener('click', function (e) {
        e.stopImmediatePropagation();
        var visible = getVisibleItems();
        var idx = getActiveIndex(visible);
        if (idx < visible.length - 1) goToThumb(visible[idx + 1]);
      }, true /* capture */);
    }

    /* ── Tab switching ─────────────────────────────────────────── */

    function switchTab(tabName) {
      var previousTab = currentTab;
      currentTab = tabName;

      if (previousTab === 'video' && tabName === 'photo') {
        pauseAllVideosInGallery(true);
      }

      // Update tab button active states
      tabBtns.forEach(function (btn) {
        var active = btn.dataset.pdpTabBtn === tabName;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      // Show / hide media items by tab (thumbs + main viewer + dots)
      applyTabVisibility(tabName);

      // Jump to first item of the new tab
      var visible = getVisibleItems();
      if (visible.length) {
        goToThumb(visible[0]);
        if (previousTab === 'video' && tabName === 'photo') {
          setTimeout(function () {
            pauseAllVideosInGallery(true);
          }, 80);
        }
      } else {
        updateArrowStates();
        updateDotsActive();
      }
    }

    if (!tabsBar || !tabsBar.hidden) {
      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { switchTab(btn.dataset.pdpTabBtn); });
      });
    }

    if (allDots.length) {
      allDots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var mediaId = dot.dataset.mediaId;
          var thumbItem = gallery.querySelector('.media-thumbs__item[data-media-id="' + mediaId + '"]');
          if (!thumbItem) return;
          if (thumbItem.dataset.pdpTab !== currentTab && tabsBar && !tabsBar.hidden) {
            switchTab(thumbItem.dataset.pdpTab);
          } else {
            goToThumb(thumbItem);
          }
        });
      });
    }

    initThumbDragScroll();

    // Keep dots in sync when user swipes/scrolls the main viewer manually.
    if (viewer) {
      viewer.addEventListener('scroll', function () {
        scheduleSyncFromSwipe(90);
      }, { passive: true });
      viewer.addEventListener('touchend', function () {
        scheduleSyncFromSwipe(40);
      }, { passive: true });
      viewer.addEventListener('mouseup', function () {
        scheduleSyncFromSwipe(40);
      });
    }

    /* ── Initialise ────────────────────────────────────────────── */

    if (tabsBar && !tabsBar.hidden) {
      // Default: show only photo thumbnails (tab bar rendered with "photo" active)
      applyTabVisibility('photo');
    } else if (dotsWrap) {
      dotsWrap.hidden = allDots.length <= 1;
    }

    // Sync arrow states after gallery.js has finished its own initialisation
    setTimeout(function () {
      updateArrowStates();
      updateDotsActive();
    }, 150);
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      initPdpMediaTabs(gallery);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
